"use client";

import type React from "react";

import {
  Code,
  Cpu,
  Database,
  GitBranch,
  Globe,
  Lightbulb,
  Palette,
  Server,
  Wrench,
  Zap,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

type Skill = {
  name: string;
  level: number; // 1-3 (Débutant, Intermédiaire, Avancé)
  category: "frontend" | "backend" | "tools" | "soft";
  icon: React.ReactNode;
  color: string;
};

const Skills = () => {
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [hoveredSkill, setHoveredSkill] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Observer pour les animations d'entrée
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const skills: Skill[] = [
    {
      name: "HTML",
      level: 3,
      category: "frontend",
      icon: <Globe size={24} />,
      color: "from-orange-500 to-red-500",
    },
    {
      name: "CSS",
      level: 3,
      category: "frontend",
      icon: <Palette size={24} />,
      color: "from-blue-500 to-indigo-500",
    },
    {
      name: "Tailwind CSS",
      level: 2,
      category: "frontend",
      icon: <Palette size={24} />,
      color: "from-cyan-500 to-blue-500",
    },
    {
      name: "JavaScript",
      level: 3,
      category: "frontend",
      icon: <Code size={24} />,
      color: "from-yellow-400 to-yellow-600",
    },
    {
      name: "TypeScript",
      level: 2,
      category: "frontend",
      icon: <Code size={24} />,
      color: "from-blue-600 to-blue-800",
    },
    {
      name: "React",
      level: 2,
      category: "frontend",
      icon: <Zap size={24} />,
      color: "from-cyan-400 to-blue-500",
    },
    {
      name: "Node.js",
      level: 3,
      category: "backend",
      icon: <Server size={24} />,
      color: "from-green-500 to-green-700",
    },
    {
      name: "Express",
      level: 2,
      category: "backend",
      icon: <Server size={24} />,
      color: "from-gray-600 to-gray-800",
    },
    {
      name: "NestJS",
      level: 1,
      category: "backend",
      icon: <Server size={24} />,
      color: "from-red-600 to-red-800",
    },
    {
      name: "SQL",
      level: 2,
      category: "backend",
      icon: <Database size={24} />,
      color: "from-blue-500 to-purple-500",
    },
    {
      name: "MongoDB",
      level: 2,
      category: "backend",
      icon: <Database size={24} />,
      color: "from-green-600 to-green-800",
    },
    {
      name: "Git",
      level: 2,
      category: "tools",
      icon: <GitBranch size={24} />,
      color: "from-orange-600 to-red-600",
    },
    {
      name: "UML",
      level: 2,
      category: "tools",
      icon: <Wrench size={24} />,
      color: "from-purple-500 to-purple-700",
    },
    {
      name: "UI/UX Design",
      level: 2,
      category: "tools",
      icon: <Palette size={24} />,
      color: "from-pink-500 to-purple-500",
    },
    {
      name: "Algorithmes",
      level: 2,
      category: "soft",
      icon: <Cpu size={24} />,
      color: "from-blue-500 to-indigo-700",
    },
    {
      name: "Analyse de Projets",
      level: 2,
      category: "soft",
      icon: <Lightbulb size={24} />,
      color: "from-yellow-500 to-amber-600",
    },
  ];

  const categories = [
    { id: "all", name: "Toutes" },
    { id: "frontend", name: "Frontend" },
    { id: "backend", name: "Backend" },
    { id: "tools", name: "Outils" },
    { id: "soft", name: "Soft Skills" },
  ];

  const filteredSkills =
    activeCategory === "all"
      ? skills
      : skills.filter((skill) => skill.category === activeCategory);

  const getLevelLabel = (level: number) => {
    switch (level) {
      case 1:
        return "Débutant";
      case 2:
        return "Intermédiaire";
      case 3:
        return "Avancé";
      default:
        return "";
    }
  };

  return (
    <section id="skills" className="py-20 overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Mes compétences
          </h2>
          <div className="h-1 w-20 bg-primary mx-auto mb-8"></div>
          <p className="text-lg text-foreground/80">
            Voici les technologies et compétences que j'ai acquises au cours de
            ma formation et que j'utilise pour créer des applications web
            modernes et performantes.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-6 py-2 rounded-full transition-colors ${
                activeCategory === category.id
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-foreground/70 hover:bg-muted/80"
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        <div
          ref={containerRef}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
        >
          {filteredSkills.map((skill, index) => (
            <div
              key={skill.name}
              className={`transform transition-all duration-500 ${
                isVisible
                  ? "translate-y-0 opacity-100"
                  : "translate-y-20 opacity-0"
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
              onMouseEnter={() => setHoveredSkill(skill.name)}
              onMouseLeave={() => setHoveredSkill(null)}
            >
              <div
                className={`
                  relative h-40 rounded-xl overflow-hidden shadow-lg 
                  transition-all duration-300 transform 
                  ${hoveredSkill === skill.name ? "scale-105 shadow-xl" : ""}
                  bg-gradient-to-br ${skill.color}
                `}
              >
                {/* Cercles décoratifs */}
                <div className="absolute top-[-20px] right-[-20px] w-24 h-24 bg-white/10 rounded-full"></div>
                <div className="absolute bottom-[-30px] left-[-20px] w-32 h-32 bg-white/5 rounded-full"></div>

                <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-white">
                  <div className="mb-2 bg-white/20 p-3 rounded-full">
                    {skill.icon}
                  </div>
                  <h3 className="text-xl font-bold text-center mb-1">
                    {skill.name}
                  </h3>

                  <div className="flex mt-2">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className={`
                          w-3 h-3 mx-1 rounded-full 
                          ${i < skill.level ? "bg-white" : "bg-white/30"}
                          ${hoveredSkill === skill.name ? "animate-pulse" : ""}
                        `}
                      ></div>
                    ))}
                  </div>

                  {hoveredSkill === skill.name && (
                    <div className="absolute bottom-2 text-sm font-medium bg-white/20 px-2 py-1 rounded-full">
                      {getLevelLabel(skill.level)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Skills;
